import { typographyTokens, layoutTokens, marketingLiftGutterSx, marketingLiftSx } from "@/shared/theme";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readPageState, rememberRoute, writePageState, useRememberedScroll } from "@/features/preferences/page-state";
import { useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Box, Button, Link, Typography, useTheme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { GameBoard } from "./GameBoard";
import { gamePerformance } from "./game-performance";
import { fetchGame, type GameListItem } from "./games.api";
import { useGamesStore } from "./games.store";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import type { CriticalMoment, CriticalMomentsReview, UserSettings } from "@chessla/shared";
import { conciseOpeningName, ecoName } from "@/shared/utils/chess";
import { GameReviewHeader, GameReviewMetadata, SelectedMovePanel } from "./components";
import { CriticalMoments, ReviewNoteEditor } from "./critical-moments";
import { recordedMovePly, reviewDecisionPosition } from "./review-decision";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { SingleGameAnalysisModal } from "@/features/analysis";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";
import { winningOutcomeMotif } from "./game-outcome";
import {
  gameReviewNeighbors,
  gameReviewPath,
  gameReviewTab,
  gamesReturnTarget,
  type GameReviewTab,
  type ReviewMoveSelection,
} from "./review-navigation";
import { useNotification } from "@/app/context/NotificationContext";
import { saveUserSettingsChange } from "@/features/preferences/save-user-settings-change";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { rememberLastReview } from "./last-review";

type ReviewStage = "think" | "test" | "verify" | "carry";
type AnalysisLoadState = "loading" | "ready" | "missing" | "error" | "complete";
const stages: ReviewStage[] = ["think", "test", "verify", "carry"];
const stageLabels: Record<ReviewStage, string> = { think: "Think", test: "Test", verify: "Verify", carry: "Carry" };
const stageDescriptions: Record<ReviewStage, string> = {
  think: "Write what you saw before revealing the answer.",
  test: "Play your idea from the exact position.",
  verify: "Compare your decision with verified evidence.",
  carry: "Save one cue for your next game.",
};

export const GameReviewPage = () => {
  const theme = useTheme();
  const { id: gameId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const reviewMode = useUserSettingsStore((state) => state.settings.reviewMode);
  const updateUserSettings = useUserSettingsStore((state) => state.update);
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const settingsOwner = useUserSettingsStore((state) => state.ownerUserId);
  const restoredView = useMemo(
    () =>
      settingsOwner
        ? readPageState<{ ply: number; moveNumber: number; san: string; tab: string }>(`game:${gameId}`)
        : undefined,
    [gameId, settingsOwner],
  );
  const cachedGames = useGamesStore((state) => state.games);
  const [currentMove, setCurrentMove] = useState<AnalyzedMoveResult | null>(null);
  const [analysisMoves, setAnalysisMoves] = useState<AnalyzedMoveResult[]>([]);
  const performance = useMemo(() => gamePerformance(analysisMoves), [analysisMoves]);
  const [showOpponentAnalysis, setShowOpponentAnalysis] = useState(true);
  const [selectedMoment, setSelectedMoment] = useState<CriticalMoment | null>(null);
  const [game, setGame] = useState<GameListItem | null>(null);
  const [gameLoadError, setGameLoadError] = useState(false);
  const [gameLoadRevision, setGameLoadRevision] = useState(0);
  const [boardLoading, setBoardLoading] = useState(true);
  const [reviewScrollElement, setReviewScrollElement] = useState<HTMLDivElement | null>(null);
  useRememberedScroll(`game:${gameId}`, reviewScrollElement, !boardLoading);
  const [analysisLoadState, setAnalysisLoadState] = useState<AnalysisLoadState>("loading");
  const [analysisRevision, setAnalysisRevision] = useState(0);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const stageHeadingRef = useRef<HTMLHeadingElement>(null);
  const boardSectionRef = useRef<HTMLDivElement>(null);
  const [stageByFact, setStageByFact] = useState<Record<string, ReviewStage>>({});
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [review, setReview] = useState<CriticalMomentsReview | null>(null);
  const analysisGate = resolveAnalysisAction(
    entitlementSnapshot,
    gameId ?? "",
    game?.analyzed ?? false,
    game?.exactTimeControl,
  );
  const initialMove = useMemo<ReviewMoveSelection | null>(() => {
    const ply = Number(searchParams.get("ply"));
    const moveNumber = Number(searchParams.get("move"));
    const san = searchParams.get("san");
    if (
      searchParams.has("ply") &&
      !searchParams.has("move") &&
      !searchParams.has("san") &&
      /^\d+$/.test(searchParams.get("ply") ?? "") &&
      Number.isSafeInteger(ply)
    )
      return { ply };
    return Number.isInteger(moveNumber) && moveNumber > 0 && san != null
      ? { moveNumber, san, ...(Number.isInteger(ply) && ply > 0 ? { ply } : {}) }
      : !searchParams.has("ply") &&
          !searchParams.has("move") &&
          !searchParams.has("san") &&
          restoredView &&
          Number.isInteger(restoredView.ply) &&
          restoredView.ply > 0 &&
          Number.isInteger(restoredView.moveNumber) &&
          typeof restoredView.san === "string"
        ? { ply: restoredView.ply, moveNumber: restoredView.moveNumber, san: restoredView.san }
        : null;
  }, [searchParams, restoredView]);
  const initialMomentFactId = searchParams.get("moment");
  const activeTab = gameReviewTab(searchParams.get("tab") ?? restoredView?.tab ?? null);
  const reviewLocationState = location.state as unknown;
  const neighbors = useMemo(() => gameReviewNeighbors(cachedGames, gameId ?? ""), [cachedGames, gameId]);
  const openNeighbor = useCallback(
    (neighborId: string | null) => {
      if (neighborId) void navigate(gameReviewPath(neighborId, activeTab), { state: reviewLocationState });
    },
    [activeTab, navigate, reviewLocationState],
  );

  useEffect(() => {
    setCurrentMove(null);
    setAnalysisMoves([]);
    setSelectedMoment(null);
    setStageByFact({});
    setCompletedIds([]);
    setReview(null);
    setGame(null);
    setBoardLoading(true);
    setAnalysisLoadState("loading");
    setAnalysisRevision(0);
    setAnalysisModalOpen(false);
  }, [gameId]);

  useEffect(() => {
    if (!gameId) return;
    let active = true;
    setGameLoadError(false);
    fetchGame(gameId)
      .then((value) => {
        if (active) {
          setGame(value);
          if (value) rememberLastReview(value.id, settingsOwner);
          setGameLoadError(false);
          if (value?.analyzed === false) {
            setAnalysisLoadState("missing");
            setBoardLoading(false);
          }
        }
      })
      .catch(() => {
        if (active) {
          setGame(null);
          setGameLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [gameId, gameLoadRevision, settingsOwner]);

  useEffect(() => {
    if (!game) {
      document.title = "CHESSLA: Game Review";
      return;
    }

    document.title = `CHESSLA: vs ${game.opponent}`;
  }, [game]);

  const handleMoveChange = useCallback(
    (move: AnalyzedMoveResult | null) => {
      setCurrentMove(move);
      if (!gameId || settingsOwner !== useUserSettingsStore.getState().ownerUserId) return;
      const position = move ? { ply: recordedMovePly(move), moveNumber: move.moveNumber, san: move.san } : undefined;
      writePageState(`game:${gameId}`, { ...(position ?? { ply: 0, moveNumber: 0, san: "" }), tab: activeTab });
      rememberRoute("/games", gameReviewPath(gameId, activeTab, position));
    },
    [gameId, activeTab, settingsOwner],
  );
  const selectTab = useCallback(
    (tab: GameReviewTab) => {
      if (!gameId) return;
      void navigate(
        gameReviewPath(gameId, tab, {
          ply: initialMove?.ply,
          moveNumber: initialMove?.moveNumber,
          san: initialMove?.san,
          momentId: initialMomentFactId,
        }),
        { replace: true },
      );
    },
    [gameId, initialMomentFactId, initialMove, navigate],
  );
  const handleCriticalMoment = useCallback(
    (moment: CriticalMoment) => {
      setSelectedMoment(moment);
      if (!gameId) return;
      void navigate(
        gameReviewPath(gameId, activeTab, {
          ply: moment.ply,
          moveNumber: Math.ceil(moment.ply / 2),
          san: moment.playedSan,
          momentId: moment.factId,
        }),
        { replace: true },
      );
    },
    [activeTab, gameId, navigate],
  );
  const handleReviewLoad = useCallback((value: CriticalMomentsReview) => setReview(value), []);
  const handleBoardLoading = useCallback((loading: boolean) => setBoardLoading(loading), []);
  const handleAnalysisState = useCallback(
    (state: Exclude<AnalysisLoadState, "complete">) => setAnalysisLoadState(state),
    [],
  );
  useEffect(() => {
    if (selectedMoment && review && !review.moments.some(({ factId }) => factId === selectedMoment.factId))
      setSelectedMoment(null);
  }, [review, selectedMoment]);
  const handleRecordedMove = useCallback(
    (move: AnalyzedMoveResult | null) => {
      setSelectedMoment(null);
      const ply = move ? recordedMovePly(move) : null;
      if (!gameId) return;
      void navigate(
        gameReviewPath(gameId, activeTab, move ? { ply, moveNumber: move.moveNumber, san: move.san } : undefined),
        { replace: true },
      );
    },
    [activeTab, gameId, navigate],
  );
  const selectedMomentMove =
    selectedMoment &&
    currentMove &&
    recordedMovePly(currentMove) === selectedMoment.ply &&
    currentMove.fenBefore === selectedMoment.fenBefore &&
    currentMove.san === selectedMoment.playedSan
      ? currentMove
      : null;
  const decisionPosition = useMemo(
    () => (selectedMoment ? reviewDecisionPosition(selectedMoment, selectedMomentMove) : null),
    [selectedMoment, selectedMomentMove],
  );
  const stage = selectedMoment ? (stageByFact[selectedMoment.factId] ?? "think") : "think";
  const revealEvidence = !selectedMoment || reviewMode === "open" || stage === "verify" || stage === "carry";
  const revealCarry = reviewMode === "open" || stage === "carry";
  const notePly = selectedMoment?.ply ?? (currentMove ? recordedMovePly(currentMove) : null);
  const setMode = (mode: UserSettings["reviewMode"]) => {
    void saveUserSettingsChange(updateUserSettings, { reviewMode: mode }, showNotification);
  };
  const setStage = (next: ReviewStage) => {
    if (!selectedMoment) return;
    setStageByFact((current) => ({ ...current, [selectedMoment.factId]: next }));
    if (next === "carry")
      setCompletedIds((current) =>
        current.includes(selectedMoment.factId) ? current : [...current, selectedMoment.factId],
      );
    requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const animationsEnabled = useUserSettingsStore.getState().settings.animationsEnabled;
      if (next === "test" && window.matchMedia(theme.breakpoints.down("md").replace("@media ", "")).matches)
        boardSectionRef.current?.scrollIntoView({
          behavior: reducedMotion || !animationsEnabled ? "auto" : "smooth",
          block: "start",
        });
      else stageHeadingRef.current?.focus();
    });
  };

  const opening = useMemo(() => {
    if (!game) return "Unknown opening";
    if (game.openingName) return conciseOpeningName(game.openingName);
    if (game.eco) return ecoName(game.eco);
    return "Unknown opening";
  }, [game]);

  if (!gameId) return null;

  return (
    <Box
      ref={setReviewScrollElement}
      data-review-mode={reviewMode}
      data-review-stage={reviewMode === "guided" ? stage : "open"}
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        overflow: { xs: "auto", sm: "hidden" },
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: layoutTokens.workspaceMaxWidth,
              mx: "auto",
              px: { ...layoutTokens.workspacePagePadding, xs: 0 },
              py: 1,
              boxSizing: "border-box",
            }}
          >
            {analysisLoadState === "missing" || analysisLoadState === "error" || analysisLoadState === "complete" ? (
              <Box
                data-analysis-recovery={analysisLoadState}
                data-lenis-prevent
                sx={{
                  minHeight: 0,
                  height: "100%",
                  overflowY: "auto",
                  overscrollBehaviorY: "contain",
                  display: "grid",
                  alignContent: "safe center",
                  borderBlock: `${layoutTokens.structuralBorder}px solid`,
                  borderColor: "divider",
                  py: { xs: 5, md: 8 },
                }}
              >
                <Typography
                  sx={{
                    color: "secondary.main",
                    fontSize: "caption.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.label,
                  }}
                >
                  GAME REVIEW /{" "}
                  {analysisLoadState === "complete"
                    ? "ANALYSIS READY"
                    : analysisLoadState === "error"
                      ? "HISTORY UNAVAILABLE"
                      : "ANALYSIS REQUIRED"}
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    mt: 1.5,
                    maxWidth: layoutTokens.readingMaxWidth,
                    fontSize: { xs: "h3.fontSize", md: "h3.fontSize" },
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.heading,
                    lineHeight: typographyTokens.lineHeight.heading,
                  }}
                >
                  {analysisLoadState === "complete"
                    ? "YOUR REVIEW IS READY."
                    : analysisLoadState === "error"
                      ? "THE REVIEW DID NOT LOAD."
                      : "THIS GAME NEEDS A FIRST READ."}
                </Typography>
                <Typography
                  sx={{
                    mt: 2.5,
                    maxWidth: layoutTokens.readingMaxWidth,
                    color: "text.secondary",
                    lineHeight: typographyTokens.lineHeight.body,
                  }}
                >
                  {analysisLoadState === "complete"
                    ? "Nikola has saved the move-by-move evidence. Open the review when you are ready to think, test, verify, and carry one lesson forward."
                    : analysisLoadState === "error"
                      ? "Your game is safe. Retry the review history before starting another analysis."
                      : `Analyze your game${game?.opponent ? ` against ${game.opponent}` : ""} on this device. The finished evidence stays attached to this exact game.`}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 3 }}>
                  {["LEGAL MOVES", "OBJECTIVE EVALUATION", "VERIFIED TURNING POINTS"].map((label) => (
                    <Box
                      key={label}
                      sx={{
                        border: layoutTokens.dividerBorder,
                        borderColor: "divider",
                        px: 1.25,
                        py: 0.75,
                        fontSize: "caption.fontSize",
                        fontWeight: "fontWeightBold",
                        letterSpacing: typographyTokens.tracking.label,
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (analysisLoadState === "missing") {
                      if (analysisGate.allowed) setAnalysisModalOpen(true);
                      else if (analysisGate.cta)
                        void navigate(analysisGate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
                    } else {
                      setAnalysisLoadState("loading");
                      setBoardLoading(true);
                      setAnalysisRevision((revision) => revision + 1);
                    }
                  }}
                  disabled={analysisLoadState === "missing" && !analysisGate.allowed && !analysisGate.cta}
                  sx={{
                    ...marketingLiftGutterSx,
                    mt: 4,
                    width: "fit-content",
                    minHeight: layoutTokens.controlHeight,
                    border: layoutTokens.structuralBorder,
                    borderColor: "background.default",
                    borderRadius: 0,
                    bgcolor: "secondary.main",
                    color: "background.default",
                    fontSize: "caption.fontSize",
                    fontWeight: "fontWeightBold",
                    boxShadow: 0,
                    ...marketingLiftSx(theme.palette.background.default),
                    "&:hover,&:focus-visible": {
                      ...marketingLiftSx(theme.palette.background.default)["&:hover, &:focus-visible"],
                      bgcolor: "secondary.main",
                    },
                  }}
                >
                  {analysisLoadState === "missing"
                    ? analysisGate.allowed
                      ? "ANALYZE THIS GAME →"
                      : analysisGate.cta === "manage_trial"
                        ? "MANAGE TRIAL →"
                        : analysisGate.cta === "join_club"
                          ? "JOIN CLUB →"
                          : "CHECKING…"
                    : analysisLoadState === "complete"
                      ? "REVIEW ANALYZED GAME →"
                      : "TRY REVIEW AGAIN →"}
                </Button>
              </Box>
            ) : (
              <Box ref={boardSectionRef} sx={{ minWidth: 0, minHeight: 0, height: "100%" }}>
                <GameBoard
                  key={`${gameId}:${analysisRevision}`}
                  gameId={gameId}
                  exactTimeControl={game?.exactTimeControl}
                  navigation={
                    <GameReviewHeader
                      onBack={() => void navigate(gamesReturnTarget(location.state))}
                      onPrevious={neighbors.previousGameId ? () => openNeighbor(neighbors.previousGameId) : null}
                      onNext={neighbors.nextGameId ? () => openNeighbor(neighbors.nextGameId) : null}
                    />
                  }
                  activeTab={activeTab}
                  onTabChange={selectTab}
                  initialMove={initialMove}
                  decisionPosition={decisionPosition}
                  revealEvidence={revealEvidence}
                  allowTemporaryPlay={reviewMode === "open" || stage !== "think"}
                  temporaryInteractive={reviewMode === "open" || stage === "test"}
                  onMoveChange={handleMoveChange}
                  onRecordedMoveSelect={handleRecordedMove}
                  onShowOpponentAnalysisChange={setShowOpponentAnalysis}
                  onLoadingChange={handleBoardLoading}
                  onAnalysisStateChange={handleAnalysisState}
                  onAnalysisMovesChange={setAnalysisMoves}
                  outcomeMotif={game ? winningOutcomeMotif(game.result, game.termination) : null}
                  reviewPanel={
                    <Box data-testid="game-review-panel-review-content" sx={{ minWidth: 0 }}>
                      {gameLoadError && (
                        <SnackbarNotice
                          severity="error"
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              onClick={() => setGameLoadRevision((revision) => revision + 1)}
                            >
                              RETRY
                            </Button>
                          }
                          sx={{ mb: 2 }}
                        >
                          Could not load this game&apos;s review details.
                        </SnackbarNotice>
                      )}
                      <Box
                        role="group"
                        aria-label="Review mode"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          mb: 2,
                          border: layoutTokens.dividerBorder,
                          borderColor: "divider",
                        }}
                      >
                        {(["guided", "open"] as const).map((mode) => (
                          <Button
                            key={mode}
                            aria-label={`${mode} review`}
                            aria-pressed={reviewMode === mode}
                            onClick={() => setMode(mode)}
                            sx={{
                              minHeight: layoutTokens.controlHeight,
                              borderRadius: 0,
                              borderInlineEnd: mode === "guided" ? layoutTokens.dividerBorder : 0,
                              borderColor: "divider",
                              display: "grid",
                              fontWeight: "fontWeightBold",
                              bgcolor: reviewMode === mode ? "primary.main" : "transparent",
                              color: reviewMode === mode ? "primary.contrastText" : "text.primary",
                            }}
                          >
                            <span>{mode.toUpperCase()}</span>
                            <Typography component="span" sx={{ fontSize: "caption.fontSize", opacity: 0.78 }}>
                              {mode === "guided" ? "Answers staged" : "Full evidence workspace"}
                            </Typography>
                          </Button>
                        ))}
                      </Box>

                      <CriticalMoments
                        gameId={gameId}
                        initialFactId={initialMomentFactId}
                        selectedFactId={selectedMoment?.factId ?? null}
                        revealEvidence={revealEvidence}
                        onLoad={handleReviewLoad}
                        onSelect={handleCriticalMoment}
                        compact={reviewMode === "guided" && selectedMoment != null}
                      />

                      {selectedMoment && reviewMode === "guided" && (
                        <>
                          <Box
                            component="nav"
                            aria-label="Review stages"
                            sx={{
                              position: { xs: "sticky", sm: "static" },
                              top: { xs: 0 },
                              zIndex: 4,
                              mb: 1,
                              bgcolor: "background.default",
                              borderBlock: { xs: layoutTokens.dividerBorder, sm: 0 },
                              borderColor: "divider",
                            }}
                          >
                            <Box
                              component="ol"
                              sx={{
                                display: { xs: "none", sm: "grid" },
                                gridTemplateColumns: "repeat(4, 1fr)",
                                m: 0,
                                p: 0,
                                listStyle: "none",
                              }}
                            >
                              {stages.map((item, index) => (
                                <Box component="li" key={item}>
                                  <Button
                                    aria-current={stage === item ? "step" : undefined}
                                    disabled={index > stages.indexOf(stage) + 1}
                                    onClick={() => setStage(item)}
                                    sx={{
                                      minHeight: layoutTokens.controlHeight,
                                      width: "100%",
                                      borderRadius: 0,
                                      px: 0.5,
                                      fontSize: "caption.fontSize",
                                    }}
                                  >
                                    {index + 1}. {item}
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                            <Box
                              sx={{
                                display: { xs: "grid", sm: "none" },
                                position: "fixed",
                                zIndex: 20,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                pb: "env(safe-area-inset-bottom)",
                                bgcolor: "background.paper",
                                borderTop: layoutTokens.structuralBorder,
                                borderColor: "primary.main",
                                boxShadow: 0,
                                gridTemplateColumns: "auto minmax(0, 1fr) auto",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                disabled={stage === "think"}
                                onClick={() => setStage(stages[stages.indexOf(stage) - 1])}
                                sx={{ minHeight: layoutTokens.controlHeight, borderRadius: 0 }}
                              >
                                Back
                              </Button>
                              <Typography
                                sx={{
                                  textAlign: "center",
                                  fontSize: "caption.fontSize",
                                  fontWeight: "fontWeightBold",
                                  textTransform: "uppercase",
                                }}
                              >
                                {stages.indexOf(stage) + 1} / 4 · {stageLabels[stage]}
                              </Typography>
                              <Button
                                aria-label={
                                  stage === "carry"
                                    ? "Review complete"
                                    : `Continue to ${stages[stages.indexOf(stage) + 1]}`
                                }
                                disabled={stage === "carry"}
                                onClick={() => setStage(stages[stages.indexOf(stage) + 1])}
                                sx={{ minHeight: layoutTokens.controlHeight, borderRadius: 0 }}
                              >
                                Continue
                              </Button>
                            </Box>
                          </Box>
                          <Box sx={{ py: 1 }}>
                            <Typography
                              ref={stageHeadingRef}
                              tabIndex={-1}
                              component="h2"
                              sx={{
                                fontSize: { xs: "h6.fontSize", sm: "h5.fontSize" },
                                fontWeight: "fontWeightBold",
                                lineHeight: typographyTokens.lineHeight.heading,
                                outline: 0,
                              }}
                            >
                              {stageLabels[stage]}
                            </Typography>
                            <Typography sx={{ mt: 0.5, color: "text.secondary" }}>
                              {stageDescriptions[stage]}
                            </Typography>
                          </Box>
                        </>
                      )}
                      {notePly &&
                        (!selectedMoment || reviewMode === "open" || stage === "think" || stage === "carry") && (
                          <ReviewNoteEditor
                            gameId={gameId}
                            ply={notePly}
                            includeReviewIdea={reviewMode === "open" || stage === "carry"}
                          />
                        )}
                      {selectedMoment && !revealEvidence && (
                        <Box sx={{ p: 1.5, my: 1, bgcolor: "action.hover" }}>
                          <Typography sx={{ fontWeight: "fontWeightBold" }}>
                            {stage === "test"
                              ? "Test your idea from the exact position."
                              : selectedMoment.decisionQuestion}
                          </Typography>
                          <Typography sx={{ mt: 0.5, color: "text.secondary" }}>
                            Verified evaluation and answer stay hidden until Verify.
                          </Typography>
                        </Box>
                      )}
                      <SelectedMovePanel
                        move={selectedMoment ? (revealEvidence ? selectedMomentMove : null) : currentMove}
                        moment={selectedMoment}
                        revealEvidence={revealEvidence}
                        revealCarry={revealCarry}
                        showOpponentEvidence={showOpponentAnalysis || selectedMoment != null}
                      />
                      {selectedMoment && revealCarry && (
                        <Box
                          component="section"
                          sx={{ display: "grid", gap: 1, p: 1.5, my: 1, bgcolor: "action.hover" }}
                        >
                          <Typography component="h3" sx={{ fontWeight: "fontWeightBold" }}>
                            Carry forward
                          </Typography>
                          <Typography>{selectedMoment.futureCue}</Typography>
                          {selectedMoment.recurrence && (
                            <Typography>
                              {selectedMoment.recurrence.references.length} same-format examples support this cue.
                            </Typography>
                          )}
                          {selectedMoment.patternIds.map((patternId) => (
                            <Link
                              key={patternId}
                              href={`/patterns?format=${review?.format ?? "rapid"}&patternId=${encodeURIComponent(patternId)}`}
                            >
                              Open Pattern evidence
                            </Link>
                          ))}
                          <Button
                            onClick={() => selectTab("nikola")}
                            sx={{ justifySelf: "start", minHeight: layoutTokens.controlHeight, borderRadius: 0, px: 0 }}
                          >
                            Ask Nikola about this exact moment
                          </Button>
                        </Box>
                      )}
                      {selectedMoment && reviewMode === "guided" && stage !== "carry" && (
                        <Button
                          onClick={() => setStage(stages[stages.indexOf(stage) + 1])}
                          sx={{
                            display: { xs: "none", sm: "inline-flex" },
                            minHeight: layoutTokens.controlHeight,
                            borderRadius: 0,
                            mb: 2,
                          }}
                        >
                          Continue to {stages[stages.indexOf(stage) + 1]}
                        </Button>
                      )}
                      {review && completedIds.length > 0 && (
                        <Box
                          aria-label="Review summary"
                          sx={{ p: 1.5, my: 2, borderTop: layoutTokens.structuralBorder, borderColor: "primary.main" }}
                        >
                          <Typography component="h3" sx={{ fontWeight: "fontWeightBold" }}>
                            What to carry into your next game
                          </Typography>
                          {review.moments
                            .filter(
                              ({ factId, selectionReason }) =>
                                completedIds.includes(factId) && selectionReason === "reusable_strength",
                            )
                            .slice(0, 1)
                            .map((moment) => (
                              <Typography key={moment.factId}>Strength · {moment.futureCue}</Typography>
                            ))}
                          {review.moments
                            .filter(
                              ({ factId, selectionReason }) =>
                                completedIds.includes(factId) && selectionReason !== "reusable_strength",
                            )
                            .slice(0, 2)
                            .map((moment) => (
                              <Typography key={moment.factId}>Cue · {moment.futureCue}</Typography>
                            ))}
                        </Box>
                      )}
                    </Box>
                  }
                  detailsPanel={
                    <Box data-testid="game-review-panel-details-content" sx={{ minWidth: 0 }}>
                      {game && <GameReviewMetadata game={game} opening={opening} performance={performance} />}
                      {game?.chessComUrl && (
                        <Box
                          component="a"
                          href={game.chessComUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            mt: 1.5,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "text.secondary",
                            fontSize: "caption.fontSize",
                            fontWeight: "fontWeightBold",
                            textDecoration: "none",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          VIEW ORIGINAL GAME <OpenInNewIcon sx={{ fontSize: "body1.fontSize" }} />
                        </Box>
                      )}
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {((analysisLoadState === "loading" && boardLoading) || (!game && !gameLoadError)) && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 30, minHeight: "100%" }}>
          <ChesslaLoadingMark fillContent page="Review" label="Preparing your game review" />
        </Box>
      )}
      <SingleGameAnalysisModal
        gameId={analysisModalOpen ? gameId : null}
        onClose={() => setAnalysisModalOpen(false)}
        onComplete={() => {
          setAnalysisModalOpen(false);
          setAnalysisLoadState("complete");
        }}
      />
    </Box>
  );
};
